import { Form, FormProps } from 'antd';
import { FC, ReactElement, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { useGetSearchParam } from '@/common/hooks';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
    PageFormFields,
    PageFormInputs,
    PageUsageForBots,
    PageUsageForKeys,
    PagesCriticalCssGenerationStatus,
    useCreatePagesMutation,
    useGetByIdLayoutsPageMutation,
    useGetByIdPageNodeQuery,
} from '@/modules/Pages';

import { PagesFormView } from './PagesFormView';
import { pagesValidationSchema } from './validation';

interface Props {
  siteId: string;
  pageId: string;
  nodeId: string;
}

export const PagesCreateFormContainer: FC<Props> = memo(({ siteId, pageId, nodeId }) => {
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageFormInputs>>(null);
  const usageForNum = useGetSearchParam('usageForNum');

  const {
    data: pageNodeData,
    error: pageNodeError,
    isLoading: isPageNodeLoading,
    isError: isPageNodeError,
  } = useGetByIdPageNodeQuery(
    siteId,
    nodeId,
  )({
    enabled: !!siteId && !!nodeId,
  });

  const { mutateAsync: getLayoutMutation } = useGetByIdLayoutsPageMutation(siteId)({
    onMutate: () => {
      setFormErrors(null);
    },
    onSuccess: (data) => {
      handleSetupBlocks(data.blocks);
    },
    onError: (err) => {
      messageOnError(err, appMessageApi);
    },
  });

  const { mutate: createPageMutation, isLoading: createSiteIsLoading } = useCreatePagesMutation(siteId)({
    onSuccess: (newPage): void => {
      appMessageApi.success('Сторінку сайту створено успішно!');

      navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', newPage.structureNode.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageFormInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  const initialValues: PageFormFields = {
    title: pageNodeData?.slug,
    usageFor: !!usageForNum,
    publishedAt: undefined,
    body: { content: '' },
    structureNodeId: pageNodeData?.id,
    layoutId: undefined,
  };

  const handleFinish = useCallback((values: PageFormFields): void => {
    setFormErrors(null);
    createPageMutation({
      ...values,
      usageFor: values.usageFor
        ? {
            [PageUsageForKeys.Bot]: PageUsageForBots.Google,
          }
        : { type: 'default' },
    });
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.PageNodesById.replace(':siteId', siteId).replace(':nodeId', nodeId));
  }, [siteId, nodeId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleKeyDown = useCallback<NonNullable<FormProps['onKeyDown']>>((e) => {
    if (e.metaKey && e.key === 's') {
      e.preventDefault();
      form.submit();
    }
  }, []);

  const handleSetupBlocks = (blocks: string[]) => {
    if (blocks.length) {
      form.setFieldValue(
        PageFormInputs.Body,
        blocks.reduce(
          (acc, block) => {
            acc[block] = '';
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
    } else {
      form.setFieldValue(PageFormInputs.Body, {});
    }
  };

  const handleGetLayoutMutation = async (id: string) => {
    await getLayoutMutation(id);
  };

  return (
    <FallbackedLayout data={pageNodeData} isLoading={isPageNodeLoading} isError={isPageNodeError} error={pageNodeError}>
      {(pageNode): ReactElement => (
        <PagesFormView
          formName="page-create"
          formType="create"
          siteId={siteId}
          pageId={pageId}
          nodeId={nodeId}
          structureNodeInfo={{
            id: pageNode.id,
            path: pageNode.path,
          }}
          previewLink={null}
          updatedAt={null}
          pages={[]}
          pageCriticalCssStatus={PagesCriticalCssGenerationStatus.NotGenerated}
          formErrors={formErrors}
          form={form}
          siteHaveSearchConsole={false}
          initialValues={initialValues}
          isFormLoading={createSiteIsLoading}
          validationSchema={pagesValidationSchema}
          handleBack={handleBack}
          handleFinish={handleFinish}
          handleFinishFailed={handleFinishFailed}
          handleKeyDown={handleKeyDown}
          handleGetLayoutMutation={handleGetLayoutMutation}
          handleOpenPushIndexingModal={undefined}
          handleOpenCriticalCssModal={undefined}
        />
      )}
    </FallbackedLayout>
  );
});
